// Types
import type { Channel } from "src/types/channel";
import type { Offer } from "src/types/offer";
import type { SponsoredAd } from "src/types/sponsored-ads";
import { type Trigger, TriggerMetricEnum, TriggerOperatorEnum } from "src/types/trigger";

// Utils
import moment from "moment";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { capitalizeFirst } from "src/design-system/core/utils/strings";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import type { MarketingAction } from "src/types/action/MarketingAction";
import type { MarketingActionScenario } from "src/types/action/MarketingActionScenario";
import type { MarketingActionSlot } from "src/types/action/MarketingActionSlot";
import { mapComparePeriodToRangeType } from "src/types/enum/AutoRulesComparePeriodEnum";
import { CompareRangeType } from "src/types/enum/CompareRangeType";
import { extendMarketingActionWithMainScenario, type FormatterOptions } from "src/utils/formatters";
import { addHundredDelta } from "src/utils/trigger";
import {
    getConceptVariantFromVariantList,
    type MarketingActionVariantEnum,
} from "src/utils/variants";
import { formatPercentValueForScenarios } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTIONS_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/config";
import { formatPrice, type FormatPriceOptions, PRICE_FACTOR } from "./money";

const PERCENT_SUFFIX = "%";
const EQUAL_SUFFIX = "(=)";

type FormatterFn = (value: number, options?: FormatPriceOptions) => string;

export const OPERATOR_SYMBOL: Record<TriggerOperatorEnum, string> = {
    [TriggerOperatorEnum.LessThan]: "<",
    [TriggerOperatorEnum.MoreThan]: "≥",
};

export const METRIC_ADORNMENT_TYPE: Record<TriggerMetricEnum, "currency" | undefined> = {
    [TriggerMetricEnum.DailyProfits]: "currency",
    [TriggerMetricEnum.DailySales]: "currency",
    [TriggerMetricEnum.DailySalesExvat]: "currency",
    [TriggerMetricEnum.DailyOrders]: undefined,
};

export const METRIC_FACTOR: Record<TriggerMetricEnum, number> = {
    [TriggerMetricEnum.DailyProfits]: PRICE_FACTOR,
    [TriggerMetricEnum.DailySales]: PRICE_FACTOR,
    [TriggerMetricEnum.DailySalesExvat]: PRICE_FACTOR,
    [TriggerMetricEnum.DailyOrders]: 1,
};

export const METRIC_FORMATTER: Record<TriggerMetricEnum, FormatterFn> = {
    [TriggerMetricEnum.DailyProfits]: formatPrice,
    [TriggerMetricEnum.DailySales]: formatPrice,
    [TriggerMetricEnum.DailyOrders]: (value) => `${value ?? "—"}`,
    [TriggerMetricEnum.DailySalesExvat]: formatPrice,
};

export function formatTriggerValue(
    trigger: Partial<Trigger>,
    formatOptions: FormatterOptions
): string {
    return METRIC_FORMATTER[trigger.metric](trigger.metaData.value, {
        ...formatOptions,
        noDecimal: true,
    });
}

export function isNewAutoRule(trigger: Partial<Trigger>): boolean {
    return !!trigger?.comparePeriod;
}

export function hasTrigger(trigger: Partial<Trigger>): boolean {
    return !!trigger?.metaData;
}

function formatOldTrigger(trigger: Partial<Trigger>, formatOptions: FormatterOptions): string {
    const { translateRaw } = formatOptions;
    const formattedTrigger = translateRaw(
        `${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.${trigger.metric}.auto-rule-trigger-reason`
    );
    const operator = OPERATOR_SYMBOL[trigger.operator];
    const triggerValue = formatTriggerValue(trigger, formatOptions);
    return `${formattedTrigger} ${operator} ${triggerValue}`;
}

function formatNewTrigger(
    trigger: Partial<Trigger>,
    isExcel,
    triggerValueSlot: number | null | undefined,
    formatOptions: FormatPriceOptions,
    isTriggerStop?: boolean,
    time?: string,
    displayValue?: string
): string {
    const { translateRaw } = formatOptions;
    const formattedTrigger = translateRaw(
        `${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.${trigger.metric}.auto-rule-trigger-reason-small`
    );

    const operator = OPERATOR_SYMBOL[trigger.operator];
    let triggerValue: string;

    if (triggerValueSlot) {
        triggerValue = formatTriggerValueSlot(
            triggerValueSlot,
            trigger.metric,
            trigger.metaData?.thresholdFactor > 0 ? "+" : null,
            formatOptions
        );
    } else {
        triggerValue = formatTriggerThreshold(
            trigger.metaData?.thresholdFactor,
            trigger.metaData?.thresholdFactor > 0 ? "+" : null
        );
    }

    if (!isExcel) {
        let translationKey = `${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.trigger-${
            isTriggerStop ? "stop" : "start"
        }-summary`;

        if (displayValue && displayValue !== "") {
            translationKey += "-custom-objective";
        }

        return translateRaw(translationKey, {
            operator,
            metric: formattedTrigger,
            time,
            objective: displayValue,
        });
    }

    const displayString = `${formattedTrigger} ${operator} ${triggerValue} ${
        trigger.metaData?.thresholdFactor === 0 ? EQUAL_SUFFIX : ""
    }`;

    const comparePeriod = translateRaw(
        `${COMMON_TRANSLATION_NAMESPACE}:enums.compare-time-range.${mapComparePeriodToRangeType(
            trigger.comparePeriod
        )}`,
        {
            displayAverage: "",
        }
    );

    const triggerChannels = trigger.metaData?.channelNames
        ?.map((elt) => translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:channel.${elt}`))
        .join(", ");

    return `${displayString} (${translateRaw(
        `${COMMON_TRANSLATION_NAMESPACE}:triggers.compare-period`
    )} : ${comparePeriod} | ${translateRaw(
        `${COMMON_TRANSLATION_NAMESPACE}:triggers.trigger-channels`
    )} : ${triggerChannels})`;
}

export function getCorrectTrigger(
    triggerStart: Partial<Trigger>,
    triggerStop: Partial<Trigger>
): Partial<Trigger> {
    let correctTrigger;

    if (!triggerStart?.metaData && !triggerStop?.metaData) {
        return null;
    }
    if (triggerStart?.metaData?.channelIds) {
        correctTrigger = triggerStart;
    } else if (triggerStop?.metaData?.channelIds) {
        correctTrigger = triggerStop;
    }
    return correctTrigger;
}
export function getChannelsNamesWithTrigger(
    triggerStart: Partial<Trigger>,
    triggerStop: Partial<Trigger>,
    allChannels: Channel[]
): string[] {
    const correctTrigger = getCorrectTrigger(triggerStart, triggerStop);
    const channelsNames = correctTrigger?.metaData?.channelIds?.map((id) => {
        return allChannels.find((allCha) => allCha.id === id)?.name;
    });
    return channelsNames;
}

export function formatTrigger({
    trigger,
    variants,
    isTriggerStop = false,
    isNewAutoRule,
    isExcel = false,
    triggerValue,
    isManagement = false,
    formatOptions,
    time,
    displayValue,
}: {
    trigger: Partial<Trigger>;
    variants: MarketingActionVariantEnum[];
    isTriggerStop?: boolean;
    isNewAutoRule: boolean;
    isExcel?: boolean;
    triggerValue?: number;
    isManagement?: boolean;
    formatOptions: FormatPriceOptions;
    time?: string;
    displayValue?: string;
}): string {
    const { translateRaw } = formatOptions;
    const concept = getConceptVariantFromVariantList(variants);
    if (trigger === undefined || trigger === null || (!hasTrigger(trigger) && isManagement)) {
        return "—";
    }
    if (!trigger.metaData && !isTriggerStop) {
        return translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:triggers.no-trigger-start`);
    }
    if (!trigger.metaData && isTriggerStop) {
        return translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:triggers.no-trigger-stop-${concept}`);
    }
    return isNewAutoRule
        ? formatNewTrigger(
              trigger,
              isExcel,
              triggerValue,
              formatOptions,
              isTriggerStop,
              time,
              displayValue
          )
        : formatOldTrigger(trigger, formatOptions);
}

export function formatTriggerStart(
    triggerStart: Trigger,
    variants: MarketingActionVariantEnum[],
    isNewAutoRule: boolean,
    triggerStartValue: number | null | undefined,
    formatOptions: FormatPriceOptions
): string {
    const { translateRaw } = formatOptions;
    return `${translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:triggers.trigger-start`)} ${formatTrigger(
        {
            trigger: triggerStart,
            variants,
            isTriggerStop: false,
            isNewAutoRule,
            isExcel: false,
            triggerValue: triggerStartValue,
            formatOptions,
        }
    )}`;
}

export function formatTriggerStop(
    triggerStop: Trigger,
    variants: MarketingActionVariantEnum[],
    isNewAutoRule: boolean,
    triggerStopValue: number | null | undefined,
    formatOptions: FormatPriceOptions
): string {
    const { translateRaw } = formatOptions;
    const formattedTriggerStop = translateRaw(
        `${COMMON_TRANSLATION_NAMESPACE}:triggers.trigger-stop`
    );
    return `${formattedTriggerStop} ${formatTrigger({
        trigger: triggerStop,
        variants,
        isTriggerStop: true,
        isNewAutoRule,
        isExcel: false,
        triggerValue: triggerStopValue,
        formatOptions,
    })}`;
}

export function formatTriggerForExcel(
    triggerStart: Trigger,
    triggerStop: Trigger,
    variants: MarketingActionVariantEnum[],
    formatOptions: FormatPriceOptions
): string {
    const { translateRaw } = formatOptions;
    const isNewAutoRuleResult = isNewAutoRule(triggerStart);
    const start = formatTriggerStart(
        triggerStart,
        variants,
        isNewAutoRuleResult,
        null,
        formatOptions
    );
    const stop = formatTriggerStop(triggerStop, variants, isNewAutoRuleResult, null, formatOptions);
    let comparePeriod = "";
    let triggerChannels = "";
    if (isNewAutoRuleResult) {
        comparePeriod = translateRaw(
            `${COMMON_TRANSLATION_NAMESPACE}:enums.compare-time-range.${mapComparePeriodToRangeType(
                triggerStart.comparePeriod
            )}`,
            {
                displayAverage: "",
            }
        );
        triggerChannels = hasTrigger(triggerStart)
            ? triggerStart.metaData?.channelNames?.join(", ")
            : triggerStop?.metaData?.channelNames?.join(", ");
    }

    return `(${translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:triggers.auto-rules`)} - ${start} | ${stop})${
        isNewAutoRuleResult
            ? ` | ${translateRaw(
                  `${COMMON_TRANSLATION_NAMESPACE}:triggers.compare-period`
              )} : ${comparePeriod} | ${translateRaw(
                  `${COMMON_TRANSLATION_NAMESPACE}:triggers.trigger-channels`
              )} : ${triggerChannels}`
            : ""
    }`;
}

export function formatTriggerThreshold(threshold: number, interval?: string): string {
    return `${interval ?? ""}${threshold}${PERCENT_SUFFIX}`;
}

export function formatTriggerValueSlot(
    triggerValue: number,
    metric: TriggerMetricEnum,
    interval: string | undefined | null,
    options: FormatPriceOptions
): string {
    return `${interval ?? ""}${
        metric === TriggerMetricEnum.DailyOrders ? triggerValue : formatPrice(triggerValue, options)
    }`;
}

export function formatTriggerPercent(percent: number): string {
    if (percent === 0) {
        return `${percent}${PERCENT_SUFFIX} ${EQUAL_SUFFIX}`;
    } else if (percent > 0) {
        return `+${percent}${PERCENT_SUFFIX}`;
    } else {
        return `${percent}${PERCENT_SUFFIX}`;
    }
}

export function isEstimatedValueTrigger(comparePeriod: CompareRangeType, startDate: Date): boolean {
    if (
        [
            CompareRangeType.weekLastYearTantamount,
            CompareRangeType.monthLastYearDateToDate,
        ].includes(comparePeriod)
    ) {
        return false;
    }
    if (moment(startDate).startOf("week") > moment()) {
        return true;
    }
    return false;
}

export function extendMarketingActionWithChannelName<T extends MarketingAction>(
    action: T,
    allChannels: Channel[]
): T {
    action?.scenarios
        ? action?.scenarios?.forEach((scenario) => {
              if (scenario.triggerStart?.comparePeriod) {
                  const channelNames = getChannelsNamesWithTrigger(
                      scenario.triggerStart,
                      scenario.triggerStop,
                      allChannels
                  );

                  if (scenario.triggerStartId && scenario.triggerStart?.metaData) {
                      scenario.triggerStart.metaData["channelNames"] = channelNames;
                  }

                  if (scenario.triggerStopId && scenario?.triggerStop?.metaData) {
                      scenario.triggerStop.metaData["channelNames"] = channelNames;
                  }
              }
          })
        : undefined;

    return action;
}

export function extendImpactedMarketingActionWithChannelName<
    T extends {
        offer?: Offer;
        sponsoredAd?: SponsoredAd;
    },
>(impactedMarketingAction: T[], allChannels: Channel[]): T[] {
    return impactedMarketingAction.map((ima) => {
        if (ima.offer) {
            ima.offer = extendMarketingActionWithMainScenario(
                extendMarketingActionWithChannelName(ima.offer, allChannels)
            );
        }

        if (ima.sponsoredAd) {
            ima.sponsoredAd = extendMarketingActionWithMainScenario(
                extendMarketingActionWithChannelName(ima.sponsoredAd, allChannels)
            );
        }

        return ima;
    });
}

export function extendMarketingActionSlotWithChannelName<
    T extends Pick<MarketingActionSlot, "scenario">,
>(slot: T, allChannels: Channel[]): T {
    if (slot?.scenario?.triggerStart?.comparePeriod) {
        const channelNames = getChannelsNamesWithTrigger(
            slot.scenario.triggerStart,
            slot.scenario.triggerStop,
            allChannels
        );
        if (slot.scenario.triggerStart?.metaData) {
            slot.scenario.triggerStart.metaData["channelNames"] = channelNames;
        }

        if (slot.scenario.triggerStop?.metaData) {
            slot.scenario.triggerStop.metaData["channelNames"] = channelNames;
        }
    }

    return slot;
}

export function formatScenarioSmartSchedulerTriggers(
    scenario: MarketingActionScenario,
    translateCommon: TFunction
): string {
    return `${capitalizeFirst(
        translateCommon(`enums.trigger-metric.${scenario?.triggerStart?.metric}`)
    )} [ ${formatPercentValueForScenarios(
        addHundredDelta(scenario?.triggerStart?.metaData?.thresholdFactor)
    )} ; ${formatPercentValueForScenarios(addHundredDelta(scenario?.triggerStop?.metaData?.thresholdFactor))} ]`;
}
